import type { Task } from "@app/core";
import { useMemo } from "react";
import { useEffectivePriorityCtx } from "../features/priority-matrix/useEffectiveMatrixDisplay.js";
import {
  useCalendarsStore,
  useLabelFilterStore,
  usePriorityFilterStore,
  useTopicFilterStore,
  useUrgencyFilterStore,
} from "../stores/index.js";
import { applyViewFilters } from "./view-filters.js";
import type { ViewFilterInputs } from "./view-filters.js";

let lastTasks: Task[] | null = null;
let lastFilters: ViewFilterInputs | null = null;
let lastResult: Task[] | null = null;

function applyViewFiltersCached(tasks: Task[], filters: ViewFilterInputs): Task[] {
  if (
    lastResult &&
    lastTasks === tasks &&
    lastFilters &&
    lastFilters.calendars === filters.calendars &&
    lastFilters.label.selected === filters.label.selected &&
    lastFilters.label.mode === filters.label.mode &&
    lastFilters.topic.selected === filters.topic.selected &&
    lastFilters.topic.mode === filters.topic.mode &&
    lastFilters.priority.selected === filters.priority.selected &&
    lastFilters.urgency.selected === filters.urgency.selected &&
    // `matrix` is a per-day/settings memoized object — stable by reference.
    lastFilters.matrix === filters.matrix
  ) {
    return lastResult;
  }
  const result = applyViewFilters(tasks, filters);
  lastTasks = tasks;
  lastFilters = filters;
  lastResult = result;
  return result;
}

/**
 * Binds the sidebar filter stores to the pure {@link applyViewFilters} chain and
 * returns a memoized `(tasks) => tasks` function. Use it at every view that
 * shows tasks (calendar routes, All-tasks list, inbox) instead of re-threading
 * the individual filters. Recomputes only when a filter selection changes.
 */
export function useViewFilters(): (tasks: Task[]) => Task[] {
  const calendars = useCalendarsStore((s) => s.calendars);
  const labelSel = useLabelFilterStore((s) => s.selected);
  const labelMode = useLabelFilterStore((s) => s.mode);
  const topicSel = useTopicFilterStore((s) => s.selected);
  const topicMode = useTopicFilterStore((s) => s.mode);
  const prioritySel = usePriorityFilterStore((s) => s.selected);
  const urgencySel = useUrgencyFilterStore((s) => s.selected);
  // Shared day-anchored context for deadline-derived urgency + priority (updates
  // at the day boundary via useToday), stable by reference within a day.
  const matrix = useEffectivePriorityCtx();

  return useMemo(
    () => (tasks: Task[]) =>
      applyViewFiltersCached(tasks, {
        calendars,
        label: { selected: labelSel, mode: labelMode },
        topic: { selected: topicSel, mode: topicMode },
        priority: { selected: prioritySel },
        urgency: { selected: urgencySel },
        matrix,
      }),
    [calendars, labelSel, labelMode, topicSel, topicMode, prioritySel, urgencySel, matrix],
  );
}
